// FRE-294: per-client MCP guide data + ordering + tick-detection.
//
// Pure logic kept out of the React layer so it's unit-testable without a DOM.
// The drawer renders one card per entry from `MCP_CLIENT_BUCKETS`, sorted via
// `sortClients` and tick-state-checked via `isClientConnected`.

import type { OnboardingState } from "@/lib/onboarding.ts";

// The 8 fixed MCP client buckets. Order here is the canonical popularity
// fallback used when no detection signal has fired — see
// apps/worker/docs/adr/0001-mcp-client-detection.md.
export const MCP_CLIENT_BUCKETS = [
  "claude-desktop",
  "claude-code",
  "cursor",
  "vscode",
  "zed",
  "codex",
  "opencode",
  "generic",
] as const;

export type McpClientBucket = (typeof MCP_CLIENT_BUCKETS)[number];

// Display labels are kept here (not derived from the bucket key) so we can
// rename a label without invalidating server-stored bucket ids.
export const MCP_CLIENT_LABELS: Record<McpClientBucket, string> = {
  "claude-desktop": "Claude Desktop",
  "claude-code": "Claude Code",
  cursor: "Cursor",
  vscode: "VS Code",
  zed: "Zed",
  codex: "Codex CLI",
  opencode: "opencode",
  generic: "Other / Generic",
};

export function isClientConnected(
  bucket: McpClientBucket,
  onboarding: OnboardingState | null,
): boolean {
  if (!onboarding) return false;
  // Detection (FRE-295) writes to mcpClientsSeen; the Generic bucket also
  // honours the self-mark in manualClientsMarked.
  if (onboarding.mcpClientsSeen[bucket]) return true;
  if (bucket === "generic" && onboarding.manualClientsMarked.generic) return true;
  return false;
}

// Detected clients (✓) bubble to the top, undetected fall back to the fixed
// popularity order in `MCP_CLIENT_BUCKETS`. Stable within each group.
export function sortClients(
  buckets: readonly McpClientBucket[],
  onboarding: OnboardingState | null,
): McpClientBucket[] {
  const indexed = buckets.map((bucket, index) => ({
    bucket,
    index,
    connected: isClientConnected(bucket, onboarding),
  }));
  indexed.sort((a, b) => {
    if (a.connected !== b.connected) return a.connected ? -1 : 1;
    return a.index - b.index;
  });
  return indexed.map((entry) => entry.bucket);
}

// Cursor and VS Code accept one-click install deep-links. We build them from
// the host URL so users don't have to copy-edit anything. Both schemes encode
// a JSON config but with different shapes — Cursor base64s a single-server
// entry; VS Code URI-encodes a `{name,url}` object.

function base64(input: string): string {
  // btoa handles ASCII; the URL we encode is always ASCII so this is safe.
  return typeof btoa !== "undefined"
    ? btoa(input)
    : // Test/Node fallback. Buffer exists in the worker too but we never run
      // mcpClients.ts there.
      Buffer.from(input, "utf-8").toString("base64");
}

export function cursorInstallLink(host: string): string {
  // Per https://cursor.com/docs/context/mcp/install-links — the `config` param
  // is the BASE64 of a single MCP server entry. For HTTP transport this is
  // just `{"url":"..."}`; Cursor infers HTTP from the presence of `url`.
  const config = base64(JSON.stringify({ url: `${host}/mcp` }));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=drop&config=${config}`;
}

export function vscodeInstallLink(host: string): string {
  // Per https://code.visualstudio.com/api/extension-guides/mcp — the query
  // string is `encodeURIComponent(JSON.stringify({ name, ...config }))`.
  const payload = JSON.stringify({ name: "drop", url: `${host}/mcp` });
  return `vscode:mcp/install?${encodeURIComponent(payload)}`;
}

export type SnippetLanguage = "json" | "shell" | "toml" | "text";

export type ClientGuide = {
  bucket: McpClientBucket;
  label: string;
  // One-line headline above the snippet ("Run this in your terminal", etc.).
  headline: string;
  // Path/instruction shown above the snippet block (e.g. "Add to
  // ~/.config/zed/settings.json").
  target: string | null;
  language: SnippetLanguage;
  snippet: string;
  // External link to the per-client markdown for users who want the long
  // version (docs/clients/<bucket>.md served from the worker).
  docsUrl: string;
  // Cursor + VS Code only. Rendered as a primary "Install in <X>" button
  // above the snippet fallback.
  installLink?: { href: string; label: string };
};

// docs/clients/<bucket>.md is checked into the repo. The worker doesn't serve
// /docs/* as a route, so we link out to the GitHub copy. Pin to `main` so the
// link survives rebases without breaking.
const DOCS_BASE = "https://github.com/sjelfull/drop/blob/main/docs/clients";

export function buildClientGuide(bucket: McpClientBucket, host: string): ClientGuide {
  const url = `${host}/mcp`;
  const docsUrl = `${DOCS_BASE}/${bucket}.md`;
  switch (bucket) {
    case "claude-desktop":
      // Claude Desktop's stable surface for remote HTTP+OAuth MCP servers is
      // Settings → Connectors (the file-config `url` field is stripped). We
      // give users the URL to paste there.
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Settings → Connectors → Add custom connector",
        target: "Paste this URL into the Integration URL field",
        language: "text",
        snippet: url,
        docsUrl,
      };
    case "claude-code":
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Run this in your terminal",
        target: null,
        language: "shell",
        snippet: `claude mcp add --transport http drop ${url}`,
        docsUrl,
      };
    case "cursor":
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Click to install — or paste the snippet into ~/.cursor/mcp.json",
        target: "~/.cursor/mcp.json",
        language: "json",
        snippet: JSON.stringify({ mcpServers: { drop: { url } } }, null, 2),
        docsUrl,
        installLink: { href: cursorInstallLink(host), label: "Install in Cursor" },
      };
    case "vscode":
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Click to install — or run the CLI command below",
        target: null,
        language: "shell",
        // `code --add-mcp` is the equivalent of the deep-link without the
        // browser handoff; useful for headless / WSL installs.
        snippet: `code --add-mcp '${JSON.stringify({ name: "drop", url })}'`,
        docsUrl,
        installLink: { href: vscodeInstallLink(host), label: "Install in VS Code" },
      };
    case "zed":
      // Zed gained native streamable-HTTP context_servers (no mcp-remote
      // bridge needed). OAuth flow runs in-app when the server returns 401.
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Add this to your Zed settings",
        target: "~/.config/zed/settings.json",
        language: "json",
        snippet: JSON.stringify({ context_servers: { drop: { url } } }, null, 2),
        docsUrl,
      };
    case "codex":
      // Codex CLI's TOML config supports `url` directly for streamable HTTP;
      // OAuth is triggered on demand via `codex mcp login drop`.
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Add this to your Codex config, then run `codex mcp login drop`",
        target: "~/.codex/config.toml",
        language: "toml",
        snippet: `[mcp_servers.drop]\nurl = "${url}"\n`,
        docsUrl,
      };
    case "opencode":
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Add this to your opencode config",
        target: "opencode.json",
        language: "json",
        snippet: JSON.stringify({ mcp: { drop: { type: "remote", url } } }, null, 2),
        docsUrl,
      };
    case "generic":
      // The catch-all bucket for any spec-compliant MCP client whose name we
      // can't normalise. We give users the raw URL + a portable JSON shape
      // and let them figure out the host-specific syntax.
      return {
        bucket,
        label: MCP_CLIENT_LABELS[bucket],
        headline: "Point your MCP client at this URL — it discovers OAuth via RFC 9728",
        target: "Server URL",
        language: "text",
        snippet: url,
        docsUrl,
      };
  }
}
